import React, { useEffect, useState } from "react";
import Template from "./Template";
import { useActions } from "../redux/actions";
import * as resetPasswordActions from "../redux/actions/resetPassword";
import * as usersActions from "../redux/actions/users";
import * as practiceActions from "../redux/actions/practice";
import { Button, Dropdown, Form, Icon, Input, Label, Message, Table } from "semantic-ui-react";

const validatePassword = (pwd: string): string => {
    if (pwd.length < 8) return "Password must be at least 8 characters long.";
    if (!/[A-Z]/.test(pwd)) return "Password must contain at least one uppercase letter.";
    if (!/[a-z]/.test(pwd)) return "Password must contain at least one lowercase letter.";
    if (!/[0-9]/.test(pwd)) return "Password must contain at least one number.";
    return "";
};

function ResetPassword() {
    const ResetPasswordActions = useActions(resetPasswordActions);
    const UsersActions = useActions(usersActions);
    const PracticeActions = useActions(practiceActions);

    const [allPractices, setAllPractices] = useState<any[]>([]);
    const [userOptions, setUserOptions] = useState<any[]>([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [username, setUsername] = useState("");
    const [practiceOpts, setPracticeOpts] = useState<any[]>([]);
    const [selectedPracticeIds, setSelectedPracticeIds] = useState<number[]>([]);
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [results, setResults] = useState<any[]>([]);
    const [submitted, setSubmitted] = useState(false);

    // Load users and practices on mount
    useEffect(() => {
        PracticeActions.getAllPractices().then((practices: any) => {
            setAllPractices(practices || []);
        });
        loadUsers(false);
    }, []);

    const loadUsers = (isHidden: boolean) => {
        setUsersLoading(true);
        UsersActions.loadUsersAndGroups({ IsHidden: isHidden }).then((result: any) => {
            const userMap: any = {};
            for (const location in result) {
                (result[location] || []).forEach((user: any) => {
                    const normalizedUserName = user.UserName.trim().toLowerCase().replace(/\s+/g, ' ');
                    if (!userMap[normalizedUserName]) {
                        userMap[normalizedUserName] = {
                            UserName: user.UserName.trim(),
                            locations: [],
                        };
                    }
                    if (!userMap[normalizedUserName].locations.includes(location)) {
                        userMap[normalizedUserName].locations.push(location);
                    }
                });
            }
            const options = Object.keys(userMap).map((normalizedKey) => ({
                key: normalizedKey,
                text: userMap[normalizedKey].UserName,
                value: userMap[normalizedKey].UserName,
                locations: userMap[normalizedKey].locations,
            }));
            setUserOptions(options);
            setUsersLoading(false);
        });
    };

    const handleUserChange = (_e: any, { value }: any) => {
        setUsername(value);
        const selectedUser: any = userOptions.find((u: any) => u.value === value);
        if (selectedUser && allPractices.length > 0) {
            const userLocations: string[] = selectedUser.locations;
            const opts = allPractices
                .filter((p: any) => userLocations.includes(p.practice.practice))
                .map((p: any) => ({
                    key: p.practiceId,
                    text: p.practice.practice,
                    value: p.practiceId,
                }));
            setPracticeOpts(opts);
            setSelectedPracticeIds(opts.map((o: any) => o.value));
        } else {
            setPracticeOpts([]);
            setSelectedPracticeIds([]);
        }
    };

    const handlePasswordChange = (val: string) => {
        setPassword(val);
        setPasswordError(validatePassword(val));
    };

    const handleSubmit = () => {
        if (!username) {
            alert("Please select a username.");
            return;
        }
        const pwdErr = validatePassword(password);
        if (pwdErr) {
            setPasswordError(pwdErr);
            return;
        }

        ResetPasswordActions.resetOdPassword({
            username,
            password,
            practiceIds: selectedPracticeIds,
        }).then((res: any) => {
            if (Array.isArray(res)) {
                setResults(res);
                setSubmitted(true);
            } else {
                alert("Failed to reset password. Please try again.");
            }
        });
    };

    const successCount = results.filter((r) => r.success).length;
    const failCount = results.filter((r) => !r.success).length;

    return (
        <Template activeLink="reset-password">
            <div className="ui card">
                <div className="content">
                    <h2>Reset OD User Password</h2>
                    <Form>
                        <Form.Field required>
                            <label>OD Username</label>
                            <Dropdown
                                placeholder={usersLoading ? "Loading users..." : "Select user..."}
                                options={userOptions}
                                selection={true}
                                search={true}
                                loading={usersLoading}
                                disabled={usersLoading}
                                value={username}
                                onChange={handleUserChange}
                                noResultsMessage="No users found."
                            />
                        </Form.Field>

                        <Form.Field>
                            <label>Practices</label>
                            <Dropdown
                                placeholder={username ? "All Practices" : "Select a user first"}
                                options={practiceOpts}
                                selection={true}
                                search={true}
                                multiple={true}
                                disabled={!username}
                                value={selectedPracticeIds}
                                onChange={(e, { value }) => setSelectedPracticeIds(value as number[])}
                            />
                            <small style={{ color: "#888" }}>
                                {!username
                                    ? "Select a user to see their practices."
                                    : selectedPracticeIds.length === 0
                                    ? "No practices selected — will reset in all practices."
                                    : `${selectedPracticeIds.length} practice(s) selected.`}
                            </small>
                        </Form.Field>

                        <Form.Field required error={!!passwordError}>
                            <label>New Password</label>
                            <Input
                                type={showPassword ? "text" : "password"}
                                placeholder="New password..."
                                value={password}
                                onChange={(e) => handlePasswordChange(e.target.value)}
                                icon={
                                    <Icon
                                        name={showPassword ? "eye slash" : "eye"}
                                        link={true}
                                        onClick={() => setShowPassword(!showPassword)}
                                    />
                                }
                            />
                            {passwordError && (
                                <small style={{ color: "red" }}>{passwordError}</small>
                            )}
                            {!passwordError && password && (
                                <small style={{ color: "green" }}>Password meets requirements.</small>
                            )}
                        </Form.Field>

                        <Form.Field>
                            <Button primary onClick={handleSubmit} disabled={usersLoading}>
                                Reset Password
                            </Button>
                        </Form.Field>
                    </Form>

                    <Message info size="small" style={{ marginTop: 8 }}>
                        <b>Password requirements:</b> at least 8 characters, one uppercase letter, one lowercase letter, and one number.
                    </Message>
                </div>
            </div>

            {submitted && results.length > 0 && (
                <div className="ui card">
                    <div className="content">
                        <h3>Results</h3>
                        <p>
                            <Label color="green">{successCount} succeeded</Label>
                            {failCount > 0 && <Label color="red" style={{ marginLeft: 8 }}>{failCount} failed</Label>}
                        </p>
                        <Table celled={true} compact={true}>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Practice</Table.HeaderCell>
                                    <Table.HeaderCell>Status</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {results.map((r: any, idx: number) => (
                                    <Table.Row key={idx} positive={r.success} negative={!r.success}>
                                        <Table.Cell>{r.practice}</Table.Cell>
                                        <Table.Cell>
                                            {r.success
                                                ? <><Icon name="check circle" color="green" /> Success</>
                                                : <><Icon name="times circle" color="red" /> Failed</>}
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                </div>
            )}
        </Template>
    );
}

export default ResetPassword;
